/**
 * Geometry for `core`.
 *
 * Everything here operates in *field space*: a normalised 0..1 square where
 * (0,0) is the top-left of the playing surface and (1,1) the bottom-right.
 * Field space is deliberately unitless and aspect-agnostic — the sport pack
 * declares the real-world aspect ratio and the renderer applies it. That is what
 * keeps geometry independent of resolution, screen size and sport.
 */

export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

export const ORIGIN: Vec2 = { x: 0, y: 0 };

export function vec(x: number, y: number): Vec2 {
  return { x, y };
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

/** Clamps a point into the unit field, optionally keeping an inset margin. */
export function clampToField(p: Vec2, inset = 0): Vec2 {
  return { x: clamp(p.x, inset, 1 - inset), y: clamp(p.y, inset, 1 - inset) };
}

/** Any two opposite corners; see `normalizeRect`. */
export interface Rect {
  readonly from: Vec2;
  readonly to: Vec2;
}

export function normalizeRect(rect: Rect): { readonly min: Vec2; readonly max: Vec2 } {
  return {
    min: { x: Math.min(rect.from.x, rect.to.x), y: Math.min(rect.from.y, rect.to.y) },
    max: { x: Math.max(rect.from.x, rect.to.x), y: Math.max(rect.from.y, rect.to.y) },
  };
}

/** The ids whose position lies within the rectangle, edges included, in `poses` order. */
export function idsInRect(rect: Rect, poses: Readonly<Record<string, Vec2>>): string[] {
  const { min, max } = normalizeRect(rect);
  return Object.entries(poses)
    .filter(([, p]) => p.x >= min.x && p.x <= max.x && p.y >= min.y && p.y <= max.y)
    .map(([id]) => id);
}

/**
 * The part of a segment `a → b` that lies inside the unit field, as the
 * parameter range `[t0, t1]` along it (Liang–Barsky), or nothing if it misses.
 * The boundary counts as inside.
 */
function segmentInField(a: Vec2, b: Vec2): [number, number] | undefined {
  let t0 = 0;
  let t1 = 1;
  const d = sub(b, a);
  // Each edge in turn: p is how fast the segment approaches it, q how far away
  // it starts. A parallel segment either lies wholly in or wholly out.
  const edges: readonly (readonly [number, number])[] = [
    [-d.x, a.x],
    [d.x, 1 - a.x],
    [-d.y, a.y],
    [d.y, 1 - a.y],
  ];
  for (const [p, q] of edges) {
    if (p === 0) {
      if (q < 0) return undefined;
      continue;
    }
    const t = q / p;
    if (p < 0) t0 = Math.max(t0, t);
    else t1 = Math.min(t1, t);
    if (t0 > t1) return undefined;
  }
  return [t0, t1];
}

/**
 * Cuts a path down to its first run inside the unit field.
 *
 * A path that starts outside begins where it enters; one that leaves ends
 * where it crosses out, and whatever follows is dropped, so an arrow keeps
 * the direction it was drawn with and simply stops at the edge. A path that
 * never touches the field, or only grazes its edge, comes back empty rather
 * than as a zero-length stub.
 */
export function clipPathToField(points: readonly Vec2[]): Vec2[] {
  const kept: Vec2[] = [];
  for (let i = 0; i + 1 < points.length; i += 1) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const span = segmentInField(a, b);
    if (!span) {
      if (kept.length > 0) break;
      continue;
    }
    const [t0, t1] = span;
    if (t0 === t1) {
      // Touched the edge at a single point: nothing of this segment is inside.
      if (kept.length > 0) break;
      continue;
    }
    if (kept.length === 0) kept.push(t0 === 0 ? a : lerpVec(a, b, t0));
    kept.push(t1 === 1 ? b : lerpVec(a, b, t1));
    if (t1 < 1) break;
  }
  return kept;
}

export function add(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function sub(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(a: Vec2, k: number): Vec2 {
  return { x: a.x * k, y: a.y * k };
}

export function length(a: Vec2): number {
  return Math.hypot(a.x, a.y);
}

export function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function normalize(a: Vec2): Vec2 {
  const len = length(a);
  return len === 0 ? ORIGIN : { x: a.x / len, y: a.y / len };
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function lerpVec(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) };
}

export function equals(a: Vec2, b: Vec2, epsilon = 1e-9): boolean {
  return Math.abs(a.x - b.x) <= epsilon && Math.abs(a.y - b.y) <= epsilon;
}

/** Angle of the vector b - a, in radians, measured from the +x axis. */
export function angleBetween(a: Vec2, b: Vec2): number {
  return Math.atan2(b.y - a.y, b.x - a.x);
}

/** Shortest distance from point `p` to the segment `a`–`b`. */
export function pointToSegmentDistance(p: Vec2, a: Vec2, b: Vec2): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lengthSquared = abx * abx + aby * aby;
  if (lengthSquared === 0) return distance(p, a);
  const t = clamp01(((p.x - a.x) * abx + (p.y - a.y) * aby) / lengthSquared);
  return distance(p, { x: a.x + abx * t, y: a.y + aby * t });
}

/** Shortest distance from point `p` to a polyline. Returns Infinity for < 2 points. */
export function pointToPolylineDistance(p: Vec2, points: readonly Vec2[]): number {
  if (points.length === 0) return Number.POSITIVE_INFINITY;
  if (points.length === 1) return distance(p, points[0]!);
  let best = Number.POSITIVE_INFINITY;
  for (let i = 1; i < points.length; i += 1) {
    const d = pointToSegmentDistance(p, points[i - 1]!, points[i]!);
    if (d < best) best = d;
  }
  return best;
}

/** Total length of a polyline in field units. */
export function polylineLength(points: readonly Vec2[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) total += distance(points[i - 1]!, points[i]!);
  return total;
}

/**
 * Ramer–Douglas–Peucker simplification. Freehand and touch input produce far more
 * samples than are worth storing or re-rendering; this keeps documents small and
 * makes serialised plays diff-friendly.
 */
export function simplifyPath(points: readonly Vec2[], tolerance = 0.004): Vec2[] {
  if (points.length <= 2) return [...points];

  const first = points[0]!;
  const last = points[points.length - 1]!;

  let maxDistance = 0;
  let index = 0;
  for (let i = 1; i < points.length - 1; i += 1) {
    const d = pointToSegmentDistance(points[i]!, first, last);
    if (d > maxDistance) {
      maxDistance = d;
      index = i;
    }
  }

  if (maxDistance <= tolerance) return [first, last];

  const left = simplifyPath(points.slice(0, index + 1), tolerance);
  const right = simplifyPath(points.slice(index), tolerance);
  return [...left.slice(0, -1), ...right];
}

/**
 * Samples a point at normalised arc-length `t` (0..1) along a polyline.
 * Used by the timeline to move an entity along a drawn movement path.
 */
export function samplePolyline(points: readonly Vec2[], t: number): Vec2 {
  if (points.length === 0) return ORIGIN;
  if (points.length === 1) return points[0]!;

  const clamped = clamp01(t);
  const total = polylineLength(points);
  if (total === 0) return points[0]!;

  let travelled = 0;
  const target = total * clamped;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1]!;
    const b = points[i]!;
    const segment = distance(a, b);
    if (travelled + segment >= target) {
      const local = segment === 0 ? 0 : (target - travelled) / segment;
      return lerpVec(a, b, local);
    }
    travelled += segment;
  }
  return points[points.length - 1]!;
}

/** Cubic ease-in-out. Kept in core so playback is identical everywhere. */
export function easeInOut(t: number): number {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

export type Easing = 'linear' | 'easeInOut';

export function applyEasing(easing: Easing, t: number): number {
  return easing === 'easeInOut' ? easeInOut(t) : clamp01(t);
}
